import * as React from 'react'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'
import { isEqual, pick } from 'lodash'

import { SRPRS } from 'components/_Assets/svg/Icon/srprs'
import { ActionList, ActionListItem } from 'components/ActionList'
import Box, { BoxType } from 'components/Box'
import { BlockItem, BlockSelect, BlockSelectType } from 'components/BlockSelect'
import { Button, TYPE as ButtonType } from 'components/Button'
import { Grid, GridGap } from 'components/Grid'
import Heading from 'components/Heading'
import Hero from 'components/Hero'
import { HeroType } from 'components/Hero/Hero'
import { InfoBox, InfoBoxItem } from 'components/InfoBox/InfoBox'
import { List, ListItem } from 'components/List'
import ListHeader from 'components/ListHeader'
import Section, {
  SectionColors,
  SectionType,
  Size,
} from 'components/Section/Section'
import Style from 'components/Style'
import { Timeline, TimelineItem } from 'components/Timeline'
import { getCdnUrl } from '../../data/api/cdn'
import { ContentProps, EntryProps } from '../../data/entries/types'
import { City } from '../../data/models/funnelOrder/cities'
import { insideEuropeOptions, outsideEuropeOptions } from '../../data/models/funnelOrder/days'
import { destinationEnum } from '../../data/models/funnelOrder/destination'
import { Theme, ThemeTypes } from '../../data/models/funnelOrder/theme'
import { getSiteCountryICC2, SiteCountry } from '../../data/models/locale'
import { getClient } from '../../data/openapi'
import { AppState } from '../../data/state'
import { FunnelStep } from '../../data/ui'
import Calendar from '../../elements/Calendar'
import FlyExcludedCities from '../../elements/excluded/FlyExcludedCities'
import FunnelFooter from '../../elements/footers/funnelFooter'
import FunnelBreadcrumbs from '../../elements/FunnelBreadcrumbs'
import GiftcardModal from '../../elements/modals/GiftcardModal'
import PersonModal from '../../elements/modals/PersonModal'
import { getNameWithTheme, getParamsWithTheme } from '../../routing/links'
import NumberOfDaysModal from '../funnel/customise/options/numberOfDays'
import People from '../funnel/customise/options/people'
import RoomType from '../funnel/customise/options/roomType'
import { QueryKey } from '../funnel/customise/wrapper'
import {
  CustomiseWrapperInnerProps,
  withAirportsAndPrices,
} from '../funnel/customise/wrapperFunction'
import { LoaderPage } from '../loader'
import DisplayContent from '../shared/content'
import Page from '../shared/page_wrapper'
import BasicInfo from './components/basicInfo'
import HowItWorks from './components/entries/howItWorks'
import FAQ from './components/faq'

interface ThemeOfTheMonthProps extends EntryProps {
  theme: Theme
  total: number
  patternImage: string
  excludedCities: City[]
  country: SiteCountry
  destination: destinationEnum
  days: number

  setExcludedCities(cities: City[]): void
  onChangeDays: (days: number) => void
  onChangeDestination: (dest: destinationEnum) => void
}

interface ThemeOfTheMonthState {
  loading: boolean
  cities: City[]
}

class ThemeOfTheMonthPage extends React.Component<
  ThemeOfTheMonthProps & CustomiseWrapperInnerProps,
  ThemeOfTheMonthState
> {
  private infoSection?: HTMLDivElement
  private detailsSection?: HTMLDivElement

  constructor(props: ThemeOfTheMonthProps & CustomiseWrapperInnerProps) {
    super(props)
    this.state = {
      loading: true,
      cities: [],
    }
  }

  public componentDidMount() {
    this.setCountries(true)
  }

  public shouldPricesUpdate(
    oldProps: ThemeOfTheMonthProps & CustomiseWrapperInnerProps,
  ) {
    const keys = ['currency', 'country', 'contentLoading', 'destination', 'days']

    return !isEqual(pick(oldProps, keys), pick(this.props, keys))
  }

  public componentDidUpdate(
    oldProps: ThemeOfTheMonthProps & CustomiseWrapperInnerProps,
  ) {
    if (this.shouldPricesUpdate(oldProps)) {
      if (!this.props.loading) {
        getClient()
      }
    }

    if (oldProps.country !== this.props.country) {
      this.setCountries()
    }
  }

  public render() {
    const {
      entry,
      match,
      prices,
      loading,
      destination,
      days,
      excludedCities,
      setExcludedCities,
    } = this.props
    const { loading: pageLoading } = this.state

    if (pageLoading) {
      return <LoaderPage />
    }

    if (entry.page === undefined) {
      throw new Error('No page properties')
    }

    const isNextStepEnabled = this.isNextStepEnabled()

    return (
      <Page
        match={match}
        page={entry.page.name}
        color={entry.colour}
        navBarType="funnel"
        navBarOverrideColour={
          entry.page.style ? entry.page.style.navbarColor : undefined
        }
        navFooter
        whiteBackground
      >
        <Hero
          blurMobile
          type={HeroType.flavourEntry}
          headingColor={
            entry.page.style ? entry.page.style.headingColor : undefined
          }
          headingText={
            <DisplayContent contentKey="title">{entry.name}</DisplayContent>
          }
          subHeading={<FunnelBreadcrumbs funnelStep={FunnelStep.Customise} />}
          src={entry.page.photos.src}
          src1024={entry.page.photos.src1024}
          src1280={entry.page.photos.src1280}
          src1600={entry.page.photos.src1600}
          src1920={entry.page.photos.src1920}
        >
          <FunnelBreadcrumbs funnelStep={FunnelStep.Customise} />
          <Heading level={1}>
            <DisplayContent contentKey="title">{entry.name}</DisplayContent>
          </Heading>
          <InfoBox>
            <InfoBoxItem
              icon={SRPRS.calendarColored()}
              firstLine={
                <DisplayContent contentKey="1-info-top">3 - 12</DisplayContent>
              }
              secondLine={
                <DisplayContent contentKey="1-info-bottom">
                  Dagen
                </DisplayContent>
              }
            />
            <InfoBoxItem
              icon={
                entry.theme === Theme.fly
                  ? SRPRS.aeroplaneColored
                  : SRPRS.carColored
              }
              firstLine={
                <DisplayContent contentKey="2-info-top">
                  destination
                </DisplayContent>
              }
              secondLine={
                <DisplayContent contentKey="2-info-bottom">
                  unknown
                </DisplayContent>
              }
            />
            <InfoBoxItem
              icon={SRPRS.mapMarkerColored}
              iconProps={{
                width: '34',
                height: '44',
                viewBox: '0 0 44 34',
                fill: 'none',
              }}
              firstLine={
                <DisplayContent contentKey="3-info-top">Binnen of</DisplayContent>
              }
              secondLine={
                <DisplayContent contentKey="3-info-bottom">
                  Buiten Europa
                </DisplayContent>
              }
            />
          </InfoBox>
        </Hero>
        <BasicInfo
          type={SectionType.transparentWhite}
          detailsScroll={this.handleDetailsScroll}
          infoScroll={this.handleInfoScroll}
        />
        <Section
          type={SectionType.white}
          size={Size.m}
          noBottomPadding
        >
          <div ref={this.setDetailsRef} />
          <Section
            type={SectionType.solidTurquoise}
            size={Size.s}
            color={SectionColors.coral}
          >
            <Style margin={[[0, 0, 20, 0]]}>
              <Timeline direction="vertical" numbers>
                <TimelineItem>
                  <Heading level={3}>
                    <DisplayContent contentKey="include-cities-heading">
                      Where would you like to go?
                    </DisplayContent>
                  </Heading>
                  {/* ------- */}
                  <BlockSelect
                    model="days"
                    nrCols={[1, 2]}
                    type={BlockSelectType.small}
                    value={destination}
                    onChange={this.handleDestinationChange}
                  >
                    <BlockItem
                      type={`checkbox`}
                      value={destinationEnum.allOfEurope}
                      labelClassName='action-list-label'
                      badge={<ActionList listClass='srprs-small-action-list'>
                        <ActionListItem
                          icon={SRPRS.aeroplaneColored}
                          listItemClass='srprs-small-list-item'
                          subject={
                            <DisplayContent contentKey="days-inside-europe-1">
                              3 - 7 DAGEN
                            </DisplayContent>
                          }
                          overrideEdit={<></>}
                          value={
                            <DisplayContent contentKey="part-of-europe-1">
                              Binnen Europa
                            </DisplayContent>
                          }
                        />
                      </ActionList>}
                    /><BlockItem
                      type={`checkbox`}
                      value={destinationEnum.outsideEurope}
                      labelClassName='action-list-label'
                      badge={<ActionList listClass='srprs-small-action-list'>
                        <ActionListItem
                          icon={SRPRS.aeroplaneColored}
                          listItemClass='srprs-small-list-item'
                          subject={
                            <DisplayContent contentKey="days-inside-europe-2">
                              7 - 14 DAGEN
                            </DisplayContent>
                          }
                          overrideEdit={<></>}
                          value={
                            <DisplayContent contentKey="part-of-europe-2">
                              Buiten Europa
                            </DisplayContent>
                          }
                        />
                      </ActionList>}
                    />
                  </BlockSelect>
                </TimelineItem>
                <TimelineItem>
                  <ActionList>
                    <People />
                    <NumberOfDaysModal options={this.handleDaysOptions()} />
                    <RoomType />
                  </ActionList>
                </TimelineItem>
                <TimelineItem>
                  <Heading level={3}>
                    <DisplayContent contentKey="exclude-cities-heading">
                      Which cities will it be?
                    </DisplayContent>
                  </Heading>

                  <FlyExcludedCities
                    solid
                    max={3}
                    destination={destination}
                    excludedCities={excludedCities}
                    setExcludedCities={setExcludedCities}
                  />
                  <Style margin={[[40, 0, 0, 0]]}>
                    <List isText>
                      <ListItem>
                        <DisplayContent contentKey={`timeline-list-item-1`}>
                          timeline item
                        </DisplayContent>
                      </ListItem>
                      <ListItem>
                        <DisplayContent contentKey={`timeline-list-item-2`}>
                          timeline item
                        </DisplayContent>
                      </ListItem>
                      <ListItem>
                        <DisplayContent contentKey={`timeline-list-item-3`}>
                          timeline item
                        </DisplayContent>
                      </ListItem>
                    </List>
                  </Style>
                </TimelineItem>
                <TimelineItem>
                  <Box fullWidthMobile>
                    <Calendar
                      actionList
                      hideTrigger
                      showCheapest={true}
                      prices={prices}
                      loading={loading}
                    />
                  </Box>
                </TimelineItem>
              </Timeline>
            </Style>
            <PersonModal />
          </Section>
        </Section>
        <FunnelFooter
          hideTotalWhenDisabled
          nextStepNo={2}
          nextStepLink={getNameWithTheme('extras')}
          nextStepLinkParams={getParamsWithTheme()}
          nextStepName={
            <DisplayContent edit={true} contentKey="extras">
              Extras
            </DisplayContent>
          }
          stickyStop
          nextStepDisabled={!isNextStepEnabled}
        />
        <GiftcardModal />
        <div ref={this.setInfoRef} />
        {this.renderFlavourBox(entry.page.content)}
        {this.renderHowItWorks()}
        <Section type={SectionType.white} size={Size.m}>
          <ListHeader noMarginBottom>
            <Button
              cutOff="none"
              type={ButtonType.plain}
              onClick={this.handleDetailsScroll}
            >
              <DisplayContent contentKey="boek-direct" edit={true}>
                Boek direct
              </DisplayContent>
            </Button>
          </ListHeader>
        </Section>
        <FAQ size={Size.m} />
      </Page>
    )
  }

  private renderHowItWorks() {
    return (
      <HowItWorks
        backgroundImageLink={'bucketlist-heading-bg.jpg'}
        boxTypeColor={BoxType.gloryColorHeaders}
      />
    )
  }

  private renderFlavourBox(content?: ContentProps) {
    const renderExplain = (leftOrRight: 'left' | 'right') => {
      let length = 5
      if (content) {
        if (leftOrRight === 'left' && content.explainLeft) {
          length = content.explainLeft
        }
        if (leftOrRight === 'right' && content.explainRight) {
          length = content.explainRight
        }
      }
      const output = []
      for (let i = 1; i <= length; i++) {
        output.push(
          <ListItem dash key={i}>
            <DisplayContent contentKey={`explain-${leftOrRight}-text-${i}`}>
              {`explain-${leftOrRight}-text-${i}`}
            </DisplayContent>
          </ListItem>,
        )
      }
      return output
    }

    return (
      <Section
        type={SectionType.photo}
        size={Size.m}
        src={{
          blurMobile: true,
          src: getCdnUrl('theme_of_the_month_2024_-_768x768.png'),
          src1024: getCdnUrl('theme_of_the_month_2024_-_1024x512.png'),
          src1280: getCdnUrl('theme_of_the_month_2024_-_1280x427.png'),
          src1600: getCdnUrl('theme_of_the_month_2024_-_1600x500.png'),
          src1920: getCdnUrl('theme_of_the_month_2024_-_1920x500.png'),
        }}
      >
        <Grid
          nrCols={[1, 2]}
          rowGap={[GridGap.m]}
          className={'grid-white-text'}
        >
          <div>
            <Heading level={4}>
              <DisplayContent contentKey="explain-left-heading">
                What we do for you
              </DisplayContent>
            </Heading>
            <List>{renderExplain('left')}</List>
          </div>
          <div>
            <Heading level={4}>
              <DisplayContent contentKey="explain-right-heading">
                What you need to arrange yourself
              </DisplayContent>
            </Heading>
            <List>{renderExplain('right')}</List>
          </div>
        </Grid>
      </Section>
    )
  }

  private isNextStepEnabled(): boolean {
    const { total } = this.props
    return (
      !!total &&
      !isNaN(total)
    )
  }

  private changeDays = (days: string) => {
    this.props.onChangeDays(+days)
  }

  private handleDaysOptions = () => this.props.destination === destinationEnum.outsideEurope ? outsideEuropeOptions : insideEuropeOptions;

  private setCountries = (mount?: boolean) => {
    getClient()
      .then(client =>
        client.apis.bookings.bookings_theme_cities({
          theme: 'theme-of-the-month',
          type: ThemeTypes.fly,
          site_country: getSiteCountryICC2(this.props.country),
        }),
      )
      .then(response => response.body)
      .then(cities => {
        this.setState({ cities, loading: false }, () => {
          if (mount) {
            const hash = window.location.hash
            if (hash !== undefined && hash === '#steden') {
              setTimeout(() => {
                if (this.detailsSection) {
                  this.detailsSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
                }
              }, 2000)
            } else {
              window.scrollTo(0, 0)
            }
          }
        })
      })
  }

  private setInfoRef = (ref: HTMLDivElement) => {
    this.infoSection = ref
  }

  private setDetailsRef = (ref: HTMLDivElement) => {
    this.detailsSection = ref
  }

  private handleInfoScroll = () => {
    if (this.infoSection) {
      this.infoSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  private handleDetailsScroll = () => {
    if (this.detailsSection) {
      this.detailsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  private cityIncluded = (cities: City[], city: City) => {
    return cities.map(x => x.id).includes(city.id)
  }

  private handleDestinationChange = (value: string) => {
    if (+value === destinationEnum.allOfEurope) {
      this.props.onChangeDestination(destinationEnum.allOfEurope);
      this.props.onChangeDays(3);
      return;
    }

    this.props.onChangeDestination(destinationEnum.outsideEurope);
    this.props.onChangeDays(7);
  }
}

const ConnectedThemeOfTheMonthPage = connect(
  (state: AppState) => ({
    theme: state.funnelOrder.theme,
    patternImage: state.page.patternImage,
    total: state.funnelOrder.total,
    excludedCities: state.funnelOrder.excludedCitiesIds,
    destination: state.funnelOrder.destination,
    days: state.funnelOrder.days,
    country: state.locale.country,
  }),
  dispatch => ({
    setExcludedCities: (cities: City[]) => {
      dispatch(actions.change('funnelOrder.excludedCitiesIds', cities))
    },
    onChangeDays: (days: number) => {
      dispatch(actions.change('funnelOrder.days', days))
    },
    onChangeDestination: (dest: destinationEnum) => {
      dispatch(actions.change('funnelOrder.destination', dest))
    },
  }),
)(ThemeOfTheMonthPage)

export default withAirportsAndPrices<EntryProps>(ConnectedThemeOfTheMonthPage, [
  QueryKey.month,
  QueryKey.year,
])
